import { createEdge, createNode } from '../../core/defaults';
import type { DiagramEdge, DiagramNode, DiagramPage, LayoutMode, NodeType } from '../../core/types';

export interface MermaidImportResult {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  warnings: string[];
  direction: 'TD' | 'TB' | 'BT' | 'LR' | 'RL';
  suggestedLayout: LayoutMode;
  lineCount: number;
}

interface ParsedNodeToken {
  id: string;
  type: NodeType;
  text: string;
  explicitShape: boolean;
}

const MAX_MERMAID_BYTES = 2_000_000;
const MAX_LINES = 10_000;
const MAX_NODES = 5_000;
const MAX_EDGES = 10_000;

function cleanLabel(raw: string): string {
  let value = raw.trim();
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    value = value.slice(1, -1);
  }
  return value
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .slice(0, 4_000);
}

function parseNodeToken(token: string): ParsedNodeToken | null {
  const trimmed = token.trim();
  const idMatch = /^([A-Za-z_][A-Za-z0-9_-]*)(.*)$/s.exec(trimmed);
  if (!idMatch) return null;
  const id = idMatch[1];
  const rest = idMatch[2].trim();
  if (!rest) return { id, type: 'rectangle', text: id, explicitShape: false };

  const shapes: Array<{ re: RegExp; type: NodeType }> = [
    { re: /^\[\[(.*)\]\]$/s, type: 'subprocess' },
    { re: /^\[\((.*)\)\]$/s, type: 'database' },
    { re: /^\(\((.*)\)\)$/s, type: 'connector' },
    { re: /^\(\[(.*)\]\)$/s, type: 'terminator' },
    { re: /^\((.*)\)$/s, type: 'ellipse' },
    { re: /^\{(.*)\}$/s, type: 'diamond' },
    { re: /^\[(.*)\]$/s, type: 'rectangle' },
  ];
  for (const shape of shapes) {
    const match = shape.re.exec(rest);
    if (match) return { id, type: shape.type, text: cleanLabel(match[1]), explicitShape: true };
  }
  return null;
}

function findEdgeOperator(line: string): { left: string; right: string; operator: string; label: string } | null {
  // Ordem importa: operadores mais específicos antes dos mais curtos.
  const operators = ['-.->', '==>', '-->', '---'];
  for (const operator of operators) {
    const index = line.indexOf(operator);
    if (index <= 0) continue;
    const left = line.slice(0, index).trim();
    let right = line.slice(index + operator.length).trim();
    let label = '';
    if (right.startsWith('|')) {
      const end = right.indexOf('|', 1);
      if (end > 0) {
        label = cleanLabel(right.slice(1, end));
        right = right.slice(end + 1).trim();
      }
    }
    return { left, right, operator, label };
  }
  return null;
}

function directionToLayout(direction: MermaidImportResult['direction']): LayoutMode {
  if (direction === 'LR' || direction === 'RL') return 'horizontal';
  return 'flow';
}

export function importMermaid(text: string): MermaidImportResult {
  if (text.length > MAX_MERMAID_BYTES) throw new Error('O arquivo Mermaid excede o limite local de 2 MB.');
  const rawLines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  if (rawLines.length > MAX_LINES) throw new Error(`O Mermaid excede o limite de ${MAX_LINES.toLocaleString('pt-BR')} linhas.`);

  let direction: MermaidImportResult['direction'] = 'TD';
  let headerSeen = false;
  const warnings: string[] = [];
  const nodesByMermaidId = new Map<string, DiagramNode>();
  const edges: DiagramEdge[] = [];

  const ensureNode = (parsed: ParsedNodeToken): DiagramNode => {
    const existing = nodesByMermaidId.get(parsed.id);
    if (existing) {
      if (parsed.explicitShape) {
        existing.type = parsed.type;
        existing.text = parsed.text || existing.text;
      }
      return existing;
    }
    if (nodesByMermaidId.size >= MAX_NODES) throw new Error(`O Mermaid excede o limite de ${MAX_NODES.toLocaleString('pt-BR')} objetos por importação.`);
    const node = createNode(parsed.type);
    node.text = parsed.text || parsed.id;
    node.data = { ...node.data, mermaidId: parsed.id };
    nodesByMermaidId.set(parsed.id, node);
    return node;
  };

  rawLines.forEach((rawLine, index) => {
    const lineNumber = index + 1;
    const line = rawLine.trim();
    if (!line || line.startsWith('%%')) return;

    const header = /^(?:flowchart|graph)\s+(TD|TB|BT|LR|RL)\s*;?$/i.exec(line);
    if (header) {
      direction = header[1].toUpperCase() as MermaidImportResult['direction'];
      headerSeen = true;
      return;
    }

    if (/^(subgraph\b|end\b)/i.test(line)) {
      warnings.push(`Linha ${lineNumber}: subgraphs ainda não são importados; a estrutura interna continuará no diagrama principal.`);
      return;
    }
    if (/^(classDef|class|style|linkStyle|click|direction)\b/i.test(line)) {
      warnings.push(`Linha ${lineNumber}: diretiva visual “${line.split(/\s+/)[0]}” ignorada na importação.`);
      return;
    }

    const edgeParts = findEdgeOperator(line.replace(/;$/, ''));
    if (edgeParts) {
      if (edges.length >= MAX_EDGES) throw new Error(`O Mermaid excede o limite de ${MAX_EDGES.toLocaleString('pt-BR')} conexões por importação.`);
      const sourceSpec = parseNodeToken(edgeParts.left);
      const targetSpec = parseNodeToken(edgeParts.right);
      if (!sourceSpec || !targetSpec) {
        warnings.push(`Linha ${lineNumber}: conexão não reconhecida e ignorada.`);
        return;
      }
      const source = ensureNode(sourceSpec);
      const target = ensureNode(targetSpec);
      const edge = createEdge(source.id, target.id);
      edge.label = edgeParts.label;
      edge.arrowEnd = edgeParts.operator !== '---';
      edge.linePattern = edgeParts.operator === '-.->' ? 'dashed' : 'solid';
      if (edgeParts.operator === '==>') edge.strokeWidth = 3;
      edges.push(edge);
      return;
    }

    const nodeSpec = parseNodeToken(line.replace(/;$/, ''));
    if (nodeSpec) {
      ensureNode(nodeSpec);
      return;
    }

    warnings.push(`Linha ${lineNumber}: sintaxe não reconhecida e ignorada.`);
  });

  if (!headerSeen) warnings.push('Cabeçalho flowchart/graph não encontrado; foi assumida direção TD.');
  if (nodesByMermaidId.size === 0) throw new Error('Nenhum objeto de flowchart reconhecido no conteúdo Mermaid.');
  if (String(direction) === 'BT' || String(direction) === 'RL') warnings.push(`Direção ${direction} foi preservada como metadado, mas o auto-layout atual usa a orientação equivalente sem inversão.`);

  return {
    nodes: [...nodesByMermaidId.values()],
    edges,
    warnings: Array.from(new Set(warnings)).slice(0, 100),
    direction,
    suggestedLayout: directionToLayout(direction),
    lineCount: rawLines.length,
  };
}

function escapeMermaidLabel(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/\r?\n/g, '<br/>')
    .replace(/\|/g, '&#124;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function nodeDefinition(id: string, node: DiagramNode): string {
  const label = `"${escapeMermaidLabel(node.text || String(node.data.hostname ?? node.type))}"`;
  switch (node.type) {
    case 'diamond':
    case 'bpmnGateway': return `${id}{${label}}`;
    case 'ellipse':
    case 'bpmnEvent':
    case 'connector': return `${id}((${label}))`;
    case 'terminator':
    case 'umlUseCase': return `${id}([${label}])`;
    case 'database':
    case 'erdEntity': return `${id}[(${label})]`;
    case 'subprocess': return `${id}[[${label}]]`;
    default: return `${id}[${label}]`;
  }
}

export function exportMermaid(page: DiagramPage, direction: MermaidImportResult['direction'] = 'TD'): string {
  const visibleNodes = page.nodes.filter((node) => !node.hidden);
  if (visibleNodes.length === 0) throw new Error('Não há objetos visíveis para exportar em Mermaid.');
  const visibleIds = new Set(visibleNodes.map((node) => node.id));
  const idMap = new Map(visibleNodes.map((node, index) => [node.id, `n${index + 1}`]));
  const lines = [`flowchart ${direction}`];
  for (const node of visibleNodes) lines.push(`  ${nodeDefinition(idMap.get(node.id)!, node)}`);
  for (const edge of page.edges) {
    if (!visibleIds.has(edge.sourceId) || !visibleIds.has(edge.targetId)) continue;
    const source = idMap.get(edge.sourceId)!;
    const target = idMap.get(edge.targetId)!;
    const operator = edge.linePattern === 'dashed' ? '-.->' : edge.arrowEnd ? '-->' : '---';
    const label = edge.label ? `|${escapeMermaidLabel(edge.label)}|` : '';
    lines.push(`  ${source} ${operator}${label} ${target}`);
  }
  return `${lines.join('\n')}\n`;
}
